import { useCallback, useState } from 'react';
import {
  Box, Flex, Text, Button, Image,
} from '@chakra-ui/react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { uploadLogo, deleteLogo } from '../api/organizationApi';

const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/svg+xml': ['.svg'],
};

export interface LogoUploadProps {
  logoUrl?: string;
  onLogoChange: (logoUrl: string | undefined) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

export function LogoUpload({ logoUrl, onLogoChange, onError, onSuccess }: LogoUploadProps) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [rejectedMessage, setRejectedMessage] = useState<string | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setRejectedMessage(null);
      setUploading(true);
      try {
        const result = await uploadLogo(file);
        onLogoChange(result.logoUrl);
        onSuccess?.(t('organization.logoUpdated'));
      } catch (err: unknown) {
        onError?.((err as Error).message);
      } finally {
        setUploading(false);
      }
    },
    [onLogoChange, onError, onSuccess, t],
  );

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const reasons = new Set(rejections.flatMap((r) => r.errors.map((e) => e.code)));
      if (reasons.has('file-too-large')) {
        setRejectedMessage(t('organization.logoTooLarge', { limit: '5 MB' }));
      } else if (reasons.has('file-invalid-type')) {
        setRejectedMessage(t('organization.logoInvalidType'));
      } else {
        setRejectedMessage(t('organization.logoUploadError'));
      }
    },
    [t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_TYPES,
    maxSize: MAX_SIZE,
    maxFiles: 1,
    multiple: false,
    onDrop,
    onDropRejected,
    disabled: uploading,
  });

  const handleDelete = async () => {
    try {
      await deleteLogo();
      onLogoChange(undefined);
      onSuccess?.(t('organization.logoDeleted'));
    } catch (err: unknown) {
      onError?.((err as Error).message);
    }
  };

  return (
    <Box data-testid="logo-upload">
      {logoUrl ? (
        <Flex gap={4} align="center" mb={3}>
          <Image
            src={logoUrl}
            alt={t('organization.logo')}
            boxSize="80px"
            borderRadius="md"
            objectFit="cover"
          />
          <Flex direction="column" gap={2}>
            <Button size="sm" {...getRootProps()} isLoading={uploading}>
              <input {...getInputProps()} />
              {t('organization.logoReplace')}
            </Button>
            <Button size="sm" colorScheme="red" variant="outline" onClick={handleDelete}>
              {t('organization.logoRemove')}
            </Button>
          </Flex>
        </Flex>
      ) : (
        <Box
          {...getRootProps()}
          border="2px dashed"
          borderColor={isDragActive ? 'brand.400' : 'gray.300'}
          borderRadius="md"
          p={6}
          textAlign="center"
          cursor="pointer"
          bg={isDragActive ? 'brand.50' : 'gray.50'}
          transition="all 0.15s"
          _hover={{ borderColor: 'brand.300', bg: 'brand.50' }}
          mb={3}
        >
          <input {...getInputProps()} />
          <Text fontSize="2xl" mb={2}>📷</Text>
          <Text fontWeight="600" fontSize="sm" color="gray.700">
            {isDragActive
              ? t('organization.logoDropHere')
              : t('organization.logoDragOrClick')}
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            {t('organization.logoFormats')}
          </Text>
        </Box>
      )}

      {rejectedMessage && (
        <Text color="red.500" fontSize="sm" mt={1}>
          {rejectedMessage}
        </Text>
      )}
    </Box>
  );
}
